import React, { useState } from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { ChartLineUp, GoogleLogo, SignOut, Compass, List, X, Flask, UsersThree, GameController, GraduationCap, Wrench, Article, LinkSimple, CaretDown, UserCircle, House, Crosshair, Siren } from "@phosphor-icons/react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import NotificationBell from "./NotificationBell";
import GwBadge from "./GwBadge";
import GlobalPlayerSearch from "./player/GlobalPlayerSearch";
import SeasonDnaDrawer from "./SeasonDnaDrawer";

const NAV = [
    { to: "/dashboard", label: "Dashboard", icon: Compass, testid: "nav-dashboard" },
    { to: "/lab", label: "Manager Lab", icon: Flask, testid: "nav-lab" },
    { to: "/tracker", label: "Tracker", icon: Crosshair, testid: "nav-tracker" },
    { to: "/hlidac", label: "Hlídač", icon: Siren, testid: "nav-hlidac", accent: "#FF8800" },
    { to: "/players", label: "Players", icon: UsersThree, testid: "nav-players" },
    { to: "/fun", label: "Fun", icon: GameController, testid: "nav-fun" },
    { to: "/academy", label: "Academy", icon: GraduationCap, testid: "nav-academy" },
];

const ADMIN_ITEMS = [
    { to: "/blog/admin", label: "Blog editor", icon: Article, testid: "nav-blog-admin" },
    { to: "/admin/duplicates", label: "Duplicity hráčů", icon: Wrench, testid: "nav-admin-duplicates" },
    { to: "/admin/analytics", label: "Analytika", icon: ChartLineUp, testid: "nav-admin-analytics" },
    { to: "/mapping", label: "Mapping", icon: LinkSimple, testid: "nav-mapping" },
];

function LangToggle() {
    const { lang, setLang } = useLang();
    const btn = (l) => `px-1.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors ${lang === l ? "bg-fa-cream text-black font-bold" : "text-white/50 hover:text-white"}`;
    return (
        <div className="hidden sm:flex items-center border border-white/15" data-testid="lang-toggle">
            <button type="button" onClick={() => setLang("cs")} className={btn("cs")} data-testid="lang-cs">CZ</button>
            <button type="button" onClick={() => setLang("en")} className={btn("en")} data-testid="lang-en">EN</button>
        </div>
    );
}

function UserMenu() {
    const { user, loading, login, logout } = useAuth();
    const navigate = useNavigate();
    if (loading) {
        return <div className="font-mono text-[10px] text-white/40 px-3 py-1.5">…</div>;
    }
    if (!user) {
        return (
            <button type="button" onClick={login} data-testid="login-google-btn"
                    className="font-mono text-xs uppercase tracking-widest px-3 py-1.5 border border-fa-cream/40 text-fa-cream hover:bg-fa-cream hover:text-black transition-colors flex items-center gap-2">
                <GoogleLogo size={14} weight="bold" />
                Sign in
            </button>
        );
    }
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button type="button" data-testid="user-menu"
                        className="flex items-center gap-2 px-2 py-1 border border-white/10 bg-black/40 hover:border-fa-cream/40 transition-colors">
                    {user.picture ? (
                        <img src={user.picture} alt="" className="w-6 h-6 rounded-full" data-testid="user-avatar" />
                    ) : (
                        <div className="w-6 h-6 rounded-full bg-fa-violet/40" />
                    )}
                    <span className="font-mono text-[10px] text-white truncate max-w-[110px]" data-testid="user-name">{user.name}</span>
                    <CaretDown size={10} className="text-white/50" />
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#0b0b0b] border border-fa-violet/30 rounded-none p-0 min-w-[200px]" data-testid="user-menu-dropdown">
                <DropdownMenuItem asChild className="p-0 focus:bg-fa-violet/10">
                    <button type="button" onClick={() => navigate("/")} data-testid="user-menu-homepage"
                            className="flex items-center gap-2 w-full px-3 py-2 font-mono text-xs uppercase tracking-widest text-white/80 hover:text-white cursor-pointer">
                        <House size={14} /> Homepage
                    </button>
                </DropdownMenuItem>
                <DropdownMenuItem asChild className="p-0 focus:bg-fa-violet/10">
                    <button type="button" onClick={() => navigate(`/u/${user.user_id}`)} data-testid="user-menu-profile"
                            className="flex items-center gap-2 w-full px-3 py-2 font-mono text-xs uppercase tracking-widest text-white/80 hover:text-white cursor-pointer">
                        <UserCircle size={14} /> Můj profil
                    </button>
                </DropdownMenuItem>
                {user.is_admin && (
                    <>
                        <DropdownMenuSeparator className="bg-fa-violet/20 m-0" />
                        <div className="px-3 pt-2 pb-1 font-mono text-[8px] uppercase tracking-widest text-fa-cream/50">Admin</div>
                        {ADMIN_ITEMS.map((it) => (
                            <DropdownMenuItem key={it.to} asChild className="p-0 focus:bg-fa-violet/10">
                                <button type="button" onClick={() => navigate(it.to)} data-testid={it.testid}
                                        className="flex items-center gap-2 w-full px-3 py-2 font-mono text-xs uppercase tracking-widest text-white/80 hover:text-white cursor-pointer">
                                    <it.icon size={14} /> {it.label}
                                </button>
                            </DropdownMenuItem>
                        ))}
                    </>
                )}
                <DropdownMenuSeparator className="bg-fa-violet/20 m-0" />
                <DropdownMenuItem asChild className="p-0 focus:bg-red-500/10">
                    <button type="button" onClick={logout} data-testid="logout-btn"
                            className="flex items-center gap-2 w-full px-3 py-2 font-mono text-xs uppercase tracking-widest text-white/60 hover:text-red-400 cursor-pointer">
                        <SignOut size={14} /> Odhlásit
                    </button>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default function AppShell({ children, liveContext }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const { user } = useAuth();
    const navItem =
        "font-mono text-xs uppercase tracking-widest px-3 py-1.5 border border-transparent hover:border-fa-cream/30 transition-colors flex items-center gap-2";
    const active = "text-fa-cream border-fa-cream/50 bg-fa-violet/20";
    const inactive = "text-white/70 hover:text-white";

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#1a0d3a] via-[#0a0615] to-[#050505] text-white">
            <header className="border-b border-fa-violet/20 bg-black/70 backdrop-blur-xl sticky top-0 z-40">
                <div className="max-w-[1600px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
                            <img src="/logo.png" alt="Fantasy Academy"
                                 className="w-11 h-11 rounded-full transition-transform group-hover:scale-105 shadow-[0_0_20px_rgba(124,77,255,0.35)]"
                                 data-testid="brand-logo" />
                            <div className="leading-none hidden sm:block">
                                <div className="font-display font-black text-base uppercase tracking-tight bg-gradient-to-r from-fa-cream via-white to-fa-mint bg-clip-text text-transparent">
                                    Fantasy Academy
                                </div>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-white/50 mt-0.5">
                                    Manager Decision System
                                </div>
                            </div>
                        </Link>
                        <Link to="/" title="Homepage" data-testid="home-icon-link"
                              className="p-1.5 border border-transparent text-white/45 hover:text-fa-mint hover:border-fa-mint/40 transition-colors">
                            <House size={15} weight="bold" />
                        </Link>
                    </div>

                    <nav className="hidden lg:flex items-center gap-1" data-testid="main-nav">
                        {NAV.map((it) => (
                            <NavLink key={it.to} to={it.to}
                                     className={({ isActive }) => `${navItem} ${isActive ? active : inactive}`}
                                     data-testid={it.testid}>
                                <it.icon size={14} weight="bold" style={it.accent ? { color: it.accent } : undefined} />
                                {it.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <GlobalPlayerSearch />
                        <GwBadge />
                        <LangToggle />
                        <SeasonDnaDrawer />
                        <NotificationBell />
                        <UserMenu />
                        <button type="button" onClick={() => setMobileOpen((v) => !v)}
                                className="lg:hidden p-1.5 border border-white/10 hover:border-fa-cream/40 text-white/80 hover:text-fa-cream transition-colors"
                                data-testid="mobile-nav-toggle"
                                aria-label={mobileOpen ? "Close menu" : "Open menu"}>
                            {mobileOpen ? <X size={16} weight="bold" /> : <List size={16} weight="bold" />}
                        </button>
                    </div>
                </div>

                {mobileOpen && (
                    <div className="lg:hidden border-t border-fa-violet/20 bg-black/95 backdrop-blur-xl max-h-[70vh] overflow-y-auto"
                         data-testid="mobile-nav-sheet">
                        <div className="px-4 py-3 space-y-0.5">
                            <div className="flex items-center gap-2 px-3 py-2">
                                <span className="font-mono text-[9px] uppercase tracking-widest text-white/40">Jazyk / Language</span>
                                <MobileLangToggle />
                            </div>
                            {NAV.map((it) => (
                                <NavLink key={it.to} to={it.to} onClick={() => setMobileOpen(false)}
                                         data-testid={`mobile-${it.testid}`}
                                         className={({ isActive }) =>
                                             `flex items-center gap-2 w-full px-3 py-2 font-mono text-xs uppercase tracking-widest transition-colors ${isActive ? "bg-fa-violet/30 text-fa-cream border-l-2 border-fa-cream" : "text-white/80 hover:text-white hover:bg-fa-violet/10 border-l-2 border-transparent"}`}>
                                    <it.icon size={14} weight="bold" />
                                    {it.label}
                                </NavLink>
                            ))}
                        </div>
                    </div>
                )}

                {liveContext && <LiveMarquee context={liveContext} />}
            </header>

            <main className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 md:py-10">{children}</main>

            <footer className="border-t border-fa-violet/20 mt-16">
                <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <img src="/logo.png" alt="" aria-hidden="true" className="w-6 h-6 rounded-full opacity-70" />
                        <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
                            © Fantasy Academy · Data by FPL &middot; Nutmeg &middot; Sportmonks &middot; football-data.org
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/" className="font-mono text-[10px] uppercase tracking-widest text-white/50 hover:text-fa-mint transition-colors" data-testid="footer-homepage-link">
                            Homepage
                        </Link>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-fa-cream/60">
                            Analyze · Scout · Select · Play · Review
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}

function MobileLangToggle() {
    const { lang, setLang } = useLang();
    const btn = (l) => `px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${lang === l ? "bg-fa-cream text-black font-bold" : "text-white/50"}`;
    return (
        <div className="flex items-center border border-white/15" data-testid="mobile-lang-toggle">
            <button type="button" onClick={() => setLang("cs")} className={btn("cs")}>CZ</button>
            <button type="button" onClick={() => setLang("en")} className={btn("en")}>EN</button>
        </div>
    );
}

function LiveMarquee({ context }) {
    const info = context.season_info || {};
    const between = (info.remaining_gameweeks ?? -1) === 0
                 && (info.max_played ?? 0) === 0
                 && (context.upcoming_fixtures?.length ?? 0) === 0;
    if (between) return null;

    const items = [];
    if (context.gameweek) items.push(`GW ${context.gameweek}`);
    const fmtTeam = (t) => `${t.name} ${t.points}pts`;
    if (context.title_race?.length)
        items.push(`TITLE RACE: ${context.title_race.map(fmtTeam).join(" / ")}`);
    if (context.europe_race?.length)
        items.push(`EUROPE: ${context.europe_race.slice(0, 5).map(fmtTeam).join(" / ")}`);
    if (context.relegation?.length)
        items.push(`RELEGATION: ${context.relegation.slice(0, 5).map(fmtTeam).join(" / ")}`);
    context.six_pointers?.slice(0, 3).forEach((sp) => {
        const tag = sp.importance_tier ? ` [${sp.importance_tier}]` : "";
        items.push(`SIX-POINTER${tag}: ${sp.home_short || sp.home} vs ${sp.away_short || sp.away}`);
    });
    context.rivalries?.slice(0, 2).forEach((r) => {
        items.push(`RIVALRY: ${r.home_short || r.home} vs ${r.away_short || r.away}`);
    });
    if (!items.length) return null;

    const doubled = [...items, ...items];
    return (
        <div className="border-t border-fa-violet/20 bg-[#050505]/80 overflow-hidden">
            <div className="flex gap-8 py-2 whitespace-nowrap animate-marquee" data-testid="live-marquee">
                {doubled.map((it, i) => (
                    <div key={`${it}-${i}`}
                         className="font-mono text-[11px] tracking-widest uppercase text-fa-cream/80 flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-fa-cream" />
                        {it}
                    </div>
                ))}
            </div>
        </div>
    );
}
